import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, RefreshCw, Loader2, Check, Palette } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../services/api';
import { toast } from 'sonner';

const CATEGORIES = [
  'Restaurant',
  'Cake & Pastry',
  'Bakery',
  'Cafe',
  'Grocery',
  'Pharmacy',
  'Florist',
  'Butcher',
  'Sushi',
  'Pizzeria',
  'Ice cream',
  'Health food',
  'Courier',
  'Logistics',
];

const VendorTemplatePicker = () => {
  const nav = useNavigate();
  const [category, setCategory] = useState('Restaurant');
  const [templates, setTemplates] = useState([]);
  const [cached, setCached] = useState(false);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(null);

  const load = async (force = false) => {
    setLoading(true);
    try {
      const { data } = await api.post('/onboarding/templates', { category, force_refresh: force });
      setTemplates(data.templates || []);
      setCached(data.cached);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load templates');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [category]);

  const pickTemplate = async (template_id) => {
    setCreating(template_id);
    try {
      const { data } = await api.post('/onboarding/use-template', { category, template_id });
      toast.success(`${data.vendor.name} is live!`);
      nav(data.redirect || '/vendor-admin');
      window.location.reload(); // pick up the new vendor_id on the user record
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not create store');
      setCreating(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3 bg-white border border-slate-200 rounded-2xl p-4">
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-wider">Category</div>
          <div className="mt-1.5 flex flex-wrap gap-2" data-testid="category-picker">
            {CATEGORIES.map((c) => (
              <button key={c} onClick={() => setCategory(c)}
                className={`h-9 px-3 rounded-full text-sm font-medium border transition-colors ${category === c ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'}`}
                data-testid={`cat-${c.toLowerCase()}-btn`}>
                {c}
              </button>
            ))}
          </div>
        </div>
        <Button variant="outline" onClick={() => load(true)} disabled={loading} data-testid="regenerate-templates-btn">
          <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Regenerate
        </Button>
      </div>

      {cached && !loading && (
        <div className="mt-3 text-[11px] text-slate-400 inline-flex items-center gap-1">
          <Sparkles className="h-3 w-3" /> Showing cached templates (last 24h). Click Regenerate for fresh ideas.
        </div>
      )}

      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {loading && templates.length === 0 && Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="h-72 bg-white border border-slate-200 rounded-2xl animate-pulse" />
        ))}
        {templates.map((t) => (
          <article key={t.template_id} className="group bg-white border border-slate-200 rounded-2xl overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all" data-testid={`template-card-${t.template_id}`}>
            <div className="h-28 relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${t.color_primary || '#10b981'}, ${t.color_secondary || '#0f172a'})` }}>
              <div className="absolute inset-0 opacity-25" style={{ backgroundImage: 'radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
              <div className="absolute bottom-2 left-3 text-white/90 text-[10px] uppercase tracking-widest">AI template</div>
            </div>
            <div className="p-4">
              <h3 className="font-semibold text-slate-900 leading-tight">{t.store_name}</h3>
              <p className="text-sm text-slate-500 mt-1 line-clamp-2">{t.tagline}</p>
              <div className="mt-3 flex items-center gap-1.5 text-[11px] text-slate-500">
                <Palette className="h-3 w-3" />
                <span className="inline-block h-3 w-3 rounded-full" style={{ background: t.color_primary }} />
                <span className="inline-block h-3 w-3 rounded-full" style={{ background: t.color_secondary }} />
                <span className="ml-2">{(t.menu_sections || []).reduce((s, x) => s + (x.items?.length || 0), 0)} items across {(t.menu_sections || []).length} sections</span>
              </div>
              <Button onClick={() => pickTemplate(t.template_id)} disabled={creating != null}
                className="mt-4 w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`use-template-${t.template_id}-btn`}>
                {creating === t.template_id ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-1.5" />}
                Use this template
              </Button>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
};

export default VendorTemplatePicker;
